import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  ModelSearchSelect,
  FilterOption,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  SearchInputIc,
  ResetFiltersIc,
  AccountIcon,
  WidgetIc,
  FilterSourceCircleIc,
} from '../../../../components/icons';
import {
  getSortOptions,
  getCampaignStatuses,
  getCampaignSources,
  getCampaignTypes,
} from '../constants';

import { FiltersProps } from '../types';

// Bolt/Lightning icon for Status filter
const BoltIcon = ({ size = 20, color = '#A8A8B0' }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.8333 1.66667L3.33333 11.6667H10L9.16667 18.3333L16.6667 8.33333H10L10.8333 1.66667Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Traffic icon for Source filter
const TrafficIcon = ({ size = 20, color = '#A8A8B0' }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 15V11M10 15V5M15 15V9"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  localFansFrom,
  localRevenueFrom,
  hasActiveFilters,
  availableModels,
  modelsLoading,
  onModelSearchChange,
  onSearchChange,
  onFansFromChange,
  onRevenueFromChange,
  onFilterChange,
  onResetFilters,
}) => {
  const { t } = useTranslations();

  return (
    <div className="px-4 pb-6 md:px-6 md:pb-6 flex items-center gap-4 flex-wrap justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap md:justify-start max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <Input
          type="text"
          placeholder={t('campaigns.filters.search.placeholder')}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm',
            inputWrapper: 'rounded-lg h-9 w-[17.0625rem] bg-auth-surface',
          }}
        />

        <FilterSelect
          placeholder={t('campaigns.filters.sort.placeholder')}
          options={getSortOptions(t).filter((opt) => opt.key !== 'all')}
          selectedKey={filters.sort === 'all' ? undefined : filters.sort}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('sort', value || 'all')}
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#A8A8B0"
                strokeLinecap="round"
                strokeWidth="1.5"
                d="M22 7H2m17 5H5m11 5H8"
              />
            </svg>
          }
          autoWidth={true}
        />

        <ModelSearchSelect
          placeholder={t('campaigns.filters.account.placeholder') || 'Аккаунт'}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.model !== 'all' ? filters.model : 'all'}
          onSelectionChange={(value) => {
            // If value is a Set (from HeroUI select), we take the first value
            // If it's a string 'all', we pass 'all'
            const selected = value === 'all' ? 'all' : value;
            onFilterChange('model', selected);
          }}
          onSearchChange={onModelSearchChange || (() => {})}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
          searchPlaceholder={t('campaigns.filters.account.searchPlaceholder')}
        />

        <FilterSelect
          placeholder={t('campaigns.filters.type.placeholder') || 'Тип кампании'}
          options={getCampaignTypes(t).filter((opt) => opt.key !== 'all')}
          selectedKey={filters.campaignType === 'all' ? undefined : filters.campaignType}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaignType', value || 'all')}
          startContent={<WidgetIc size={20} fill="#A8A8B0" />}
          autoWidth={true}
        />

        <FilterSelect
          placeholder={t('campaigns.filters.status.placeholder')}
          options={getCampaignStatuses(t).filter((opt) => opt.key !== 'all')}
          selectedKey={filters.status === 'all' ? undefined : filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value || 'all')}
          startContent={<BoltIcon size={24} />}
          autoWidth={true}
        />

        <FilterSelect
          placeholder={t('campaigns.filters.sources.placeholder')}
          options={getCampaignSources(t).filter((opt) => opt.key !== 'all')}
          selectedKey={filters.source === 'all' ? undefined : filters.source}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('source', value || 'all')}
          startContent={<FilterSourceCircleIc size={20} />}
          autoWidth={true}
        />

        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          startContent={
            <span className="text-auth-muted text-sm whitespace-nowrap mr-1">
              {t('campaigns.filters.fansFrom') || 'Фанатов от'}:
            </span>
          }
          size="sm"
          value={localFansFrom}
          onChange={(e) => {
            const sanitized = e.target.value.replace(/[^0-9]/g, '');
            onFansFromChange(sanitized);
          }}
          classNames={{
            mainWrapper: 'w-fit max-w-[12.5rem]',
            base: 'w-fit max-w-[12.5rem]',
            input: 'text-sm text-auth-muted !pl-0',
            inputWrapper: 'rounded-lg h-9 bg-auth-surface px-3',
            innerWrapper: 'gap-0',
          }}
        />

        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          startContent={
            <span className="text-auth-muted text-sm whitespace-nowrap mr-1">
              {t('campaigns.filters.revenueFrom') || 'Доход от'}:
            </span>
          }
          size="sm"
          value={localRevenueFrom}
          onChange={(e) => {
            const sanitized = e.target.value.replace(/[^0-9]/g, '');
            onRevenueFromChange(sanitized);
          }}
          classNames={{
            mainWrapper: 'w-fit max-w-[12.5rem]',
            base: 'w-fit max-w-[12.5rem]',
            input: 'text-sm text-auth-muted !pl-0',
            inputWrapper: 'rounded-lg h-9 bg-auth-surface px-3',
            innerWrapper: 'gap-0',
          }}
        />
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && (
          <Tooltip content={t('campaigns.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
